/**
 * A aritmética de enquadrar uma imagem num quadro — zoom e encaixe.
 *
 * Mora fora do componente porque é CONTA, e conta se testa executando. O
 * resto da suíte deste projeto lê código-fonte e cobra decisões, o que
 * funciona para decisão e não funciona para fórmula: um zoom que foge do
 * cursor passa em qualquer teste de leitura e é insuportável de usar.
 */

export interface Ponto { x: number; y: number }

/** Limites de zoom. Abaixo some, acima vira mosaico de pixel. */
export const ESCALA_MIN = 0.02;
export const ESCALA_MAX = 12;

/**
 * O maior encaixe da imagem inteira dentro do quadro, centralizado.
 *
 * `Math.min` das duas razões — o lado que aperta primeiro é quem manda.
 * Com `max`, a imagem transborda e some parte dela sem aviso.
 */
export function encaixeDeConter(
  imagem: { largura: number; altura: number },
  quadro: { largura: number; altura: number },
): { escala: number; desloc: Ponto } {
  if (!imagem.largura || !imagem.altura || !quadro.largura || !quadro.altura) {
    return { escala: 1, desloc: { x: 0, y: 0 } };
  }
  const escala = Math.min(
    quadro.largura / imagem.largura,
    quadro.altura / imagem.altura,
  );
  return {
    escala,
    desloc: {
      x: (quadro.largura - imagem.largura * escala) / 2,
      y: (quadro.altura - imagem.altura * escala) / 2,
    },
  };
}

/**
 * Zoom ANCORADO num ponto: o pixel sob o cursor continua sob o cursor.
 *
 * É a diferença entre reenquadrar e brigar com a tela. Sem a âncora, o
 * zoom acontece no canto do quadro, a imagem foge, e ampliar um detalhe
 * vira tentativa e erro — amplia, arrasta de volta, amplia de novo.
 *
 * A conta: o ponto do quadro `c` corresponde ao ponto `(c - d) / s` da
 * imagem. Pra esse mesmo ponto da imagem continuar em `c` depois da troca
 * de escala, o novo deslocamento é `c - ponto * novaEscala`.
 */
export function zoomAncorado(
  atual: { escala: number; desloc: Ponto },
  fator: number,
  ancora: Ponto,
): { escala: number; desloc: Ponto } {
  const nova = Math.min(ESCALA_MAX, Math.max(ESCALA_MIN, atual.escala * fator));
  return {
    escala: nova,
    desloc: {
      x: ancora.x - ((ancora.x - atual.desloc.x) / atual.escala) * nova,
      y: ancora.y - ((ancora.y - atual.desloc.y) / atual.escala) * nova,
    },
  };
}

/**
 * Quantas vezes o quadro é ampliado na exportação.
 *
 * Dois tetos, e os dois importam. `ladoMax` evita arquivo gigante por
 * nada — planta é desenho de linha visto de longe, e o limite de upload
 * chega antes da nitidez. O segundo impede AMPLIAR além do que a imagem
 * tem: interpolar pixel que não existe não acrescenta detalhe, só peso.
 */
export function fatorDeExportacao(
  quadro: { largura: number; altura: number },
  imagemLargura: number,
  escala: number,
  ladoMax: number,
): number {
  if (!quadro.largura || !quadro.altura) return 1;
  const porTeto = ladoMax / Math.max(quadro.largura, quadro.altura);
  const porResolucao = Math.max(1, (imagemLargura * escala) / quadro.largura);
  return Math.max(0.01, Math.min(porTeto, porResolucao));
}
